import {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';

import { useStore } from '@nanostores/react';
import { lruMemoize } from '@reduxjs/toolkit';
import classNames from 'classnames';
import { VList, type VListHandle } from 'virtua';

import { getUserLabel } from '@iconik-io/core/src/get_user_label';
import { nameInitials } from '@iconik-io/core/src/name_initials';
import type {
  AssetsSegmentSchemaPatched,
  AssetsSegmentSchemaWithIdPatched,
} from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import type {
  AssetsGetShareSchemaWithIdNullable,
  AssetsHttpPostAssetsByAssetIdSegmentsHeader,
  AssetsSegmentSchemaWithId,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  httpGetGroupsBasic,
  httpGetTeamsBasic,
  httpGetUsersBasic,
  httpGetUsersByUserId,
} from '@iconik-io/iconik-api/src/services/users';
import {
  Avatar,
  Button,
  Focusable,
  Icon,
  Menu,
  type MenuItemWithId,
  MenuTrigger,
  ProgressCircle,
  SearchField,
  Sheet,
  type SuggestionsPluginItem,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@iconik-io/uad';
import { useBasePlayerContext } from '@iconik-io/unified-player-ui-react';

import { FLAG_SIMPLIFIED_ACLS } from '../../../../analytics/flags';
import { growthbook } from '../../../../analytics/growthbook';
import { segmentTrackEvent } from '../../../../redux/analytics/thunks';
import { selectUserRoles } from '../../../../redux/auth/selectors';
import { sendNewComment } from '../../../../redux/comment_attachments/thunks';
import { selectAssetOrSearchItem } from '../../../../redux/entities/selectors/assets';
import { selectClosedProxyByVersionId } from '../../../../redux/entities/selectors/proxies';
import {
  selectAssetVersionsExtended,
  selectAssetVersionsExtendedMap,
  selectIsLatestVersion,
} from '../../../../redux/entities/selectors/versions';
import { useAppDispatch, useAppSelector } from '../../../../redux/redux_hooks';
import { filterExtendedVersionsBySelectedVersionIds } from '../../../../utils/filter_versions_by_selected_version_ids';
import { getPlaylistItemVersionIds } from '../../../../utils/get_playlist_item_version_ids';
import { getProxyMediaType } from '../../../../utils/get_proxy_media_type';
import { getProxyTimecodeOptions } from '../../../../utils/get_proxy_timecode_options';
import { seekableMediaTypes } from '../../../../utils/media_type';
import type { PlaylistItemVersionParam } from '../../../../utils/types/playlist';
import { useLatest } from '../../../hooks/use_latest';
import { useVirtualizedScrollToIds } from '../../../hooks/use_virtualized_scroll_to_ids';
import {
  CommentAttachmentPreview,
  type CommentAttachmentPreviewItem,
} from '../../shared/comment_attachments/comment_attachment_preview/comment_attachment_preview';
import { usePendingAttachments } from '../../shared/comment_attachments/hooks/use_pending_attachments';
import { getPendingFormAttachments } from '../../shared/comment_attachments/utils/get_pending_form_attachments';
import { CommentForm } from '../../shared/comment_form/comment_form';
import { segmentColors } from '../../shared/segment/constants';
import { Segment } from '../../shared/segment/segment';
import { ColorMenuItem } from '../../shared/ui/color_menu_item/color_menu_item';
import { LoadingBar } from '../../shared/ui/loading_bar/loading_bar';
import { EmptyState } from '../empty_state/empty_state';
import { CommentsMenuButton } from './comments_menu_button';
import { CommentsSortMenu } from './comments_sort_menu';
import { getIsAllowedDeletingComments } from './get_is_allowed_deleting_comments';
import { getIsAllowedEditingComments } from './get_is_allowed_editing_comments';
import { useAutoAnnotation } from './use_auto_annotation';
import {
  type CommentSortExpression,
  useCommentEntityList,
} from './use_comment_entity_list';

import styles from './comments_panel.module.css';

type CommentsPanelProps = {
  assetId: string;
  isDisabled: boolean;
  versionId: string | undefined;
  selectedSegmentIds?: string[];
  shareLinkUserEmail?: string;
  onSubmitComment?: (segment: AssetsSegmentSchemaWithId) => void;
  onAutoAnnotationCreated?: (segment: AssetsSegmentSchemaWithId) => void;
  onReplyCreated?: (reply: AssetsSegmentSchemaWithId) => void;
  share?: AssetsGetShareSchemaWithIdNullable;
  userId?: string | undefined;
  shareUserEmail?: string | undefined;
  shareSessionId?: string | undefined;
  isInternalUser: boolean;
  canCreateComment?: boolean;
  onIdentificationRequired?: () => void;
  onReplyClick?: () => void;
  shouldOpenReplyForm?: boolean;
  canMarkCommentComplete?: boolean;
  onMarkCompleteBlocked?: () => void;
  originalPlaylistItemVersionId?: PlaylistItemVersionParam;
};

type SegmentRepliesVisible = {
  [segmentId: string]: boolean;
};

const getListIds = lruMemoize(
  (
    comments: AssetsSegmentSchemaWithIdPatched[],
    repliesMap: Record<string, AssetsSegmentSchemaWithIdPatched[]>,
    visibleReplies: SegmentRepliesVisible,
  ) => {
    const ids: string[] = [];
    for (const comment of comments) {
      ids.push(comment.id);
      if (visibleReplies[comment.id]) {
        const replies = repliesMap[comment.id];
        if (replies) {
          ids.push(...replies.map((reply) => reply.id));
        }
      }
    }
    return ids;
  },
);

export function CommentsPanel({
  assetId,
  isDisabled,
  selectedSegmentIds,
  shareLinkUserEmail,
  onSubmitComment,
  onAutoAnnotationCreated,
  onReplyCreated,
  share,
  userId,
  shareUserEmail,
  shareSessionId,
  isInternalUser,
  versionId: currentVersionId,
  canCreateComment = true,
  onIdentificationRequired,
  onReplyClick,
  shouldOpenReplyForm,
  canMarkCommentComplete,
  onMarkCompleteBlocked,
  originalPlaylistItemVersionId,
}: CommentsPanelProps) {
  const dispatch = useAppDispatch();

  const roles = useAppSelector(selectUserRoles);
  const asset = useAppSelector((state) =>
    selectAssetOrSearchItem(state, assetId),
  );
  const usersBasicById = useAppSelector((state) => state.entities.users_basic);

  const [returnPlayerToPlayback, setReturnPlayerToPlayback] = useState(false);

  const assetProxy = useAppSelector((state) =>
    selectClosedProxyByVersionId(state, assetId, currentVersionId),
  );

  const proxyTimecodeOptions = assetProxy
    ? getProxyTimecodeOptions(assetProxy)
    : undefined;
  const assetVersionsMap = useAppSelector((state) =>
    selectAssetVersionsExtendedMap(state, assetId),
  );
  const assetVersions = useAppSelector((state) =>
    selectAssetVersionsExtended(state, assetId),
  );

  const selectedVersions = getPlaylistItemVersionIds(
    originalPlaylistItemVersionId,
  );

  const filteredVersionsBySelectedVersionIds =
    filterExtendedVersionsBySelectedVersionIds(assetVersions, selectedVersions);

  const isLatestVersion = useAppSelector((state) =>
    selectIsLatestVersion(state, assetId, currentVersionId),
  );

  const { store: unifiedPlayerStore } = useBasePlayerContext();
  const unifiedSelectedSegmentIds = useStore(
    unifiedPlayerStore.segment.$selectedSegmentIds,
  );

  const activeTool = useStore(unifiedPlayerStore.$activeTool);
  const previousActiveTool = useStore(unifiedPlayerStore.$previousActiveTool);

  const [segment, setSegment] = useState<AssetsSegmentSchemaPatched>({
    segment_type: 'COMMENT',
    asset_id: assetId,
    version_id: currentVersionId,
    segment_text: '',
    segment_color: undefined,
  });
  const [commentFormResetCounter, setCommentFormResetCounter] = useState(0);

  const attachments = usePendingAttachments();
  const {
    reset: resetAttachments,
    claim: claimAttachments,
    complete: completeAttachments,
    release: releaseAttachments,
  } = attachments;
  const latestFormRef = useLatest({ assetId, currentVersionId });
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const [previewAttachment, setPreviewAttachment] =
    useState<CommentAttachmentPreviewItem>();

  const seekable = seekableMediaTypes.has(getProxyMediaType(assetProxy));

  const [sort, setSort] = useState<CommentSortExpression>('date_created,desc');

  useEffect(() => {
    setSort(seekable ? 'time_start_milliseconds,asc' : 'date_created,desc');
  }, [seekable]);

  const [versionFilter, setVersionFilter] = useState<string | undefined>(
    currentVersionId,
  );
  const [annotationFilter, setAnnotationFilter] = useState<string[]>([]);
  const [completionFilter, setCompletionFilter] = useState<string[]>([]);
  const [replyFilter, setReplyFilter] = useState<string[]>([]);
  const [mentionFilter, setMentionFilter] = useState<string[]>([]);
  const [commenterFilter, setCommenterFilter] = useState<string[]>([]);
  const [colorFilter, setColorFilter] = useState<string[]>([]);
  const [visibilityFilter, setVisibilityFilter] = useState<string[]>([]);
  const [textFilter, setTextFilter] = useState<string | undefined>(undefined);
  const [showSearchField, setShowSearchField] = useState(false);
  const [showReplies, setShowReplies] = useState<SegmentRepliesVisible>({});
  const {
    entities: comments,
    repliesMap,
    rootIdsWithMatchingReply,
    totalRepliesCountMap,
    baseEntities,
    appendId,
    isLoading,
    fetchStatus,
    reset,
  } = useCommentEntityList(
    assetId,
    {
      annotationFilter,
      completionFilter,
      replyFilter,
      mentionFilter,
      commenterFilter,
      colorFilter,
      visibilityFilter,
      textFilter,
      versionFilter: asset?.type === 'SUBCLIP' ? 'all' : versionFilter,
      sort,
    },
    share?.id,
    share?.object_type ?? undefined,
    share?.show_existing_comments ?? undefined,
    isInternalUser,
    originalPlaylistItemVersionId,
  );

  const handleChange = useCallback(
    (data: Partial<AssetsSegmentSchemaPatched>) => {
      setSegment((prevData) => ({ ...prevData, ...data }));
    },
    [],
  );

  useEffect(() => {
    if (assetId && currentVersionId) {
      setSegment({
        segment_type: 'COMMENT',
        asset_id: assetId,
        version_id: currentVersionId,
        segment_text: '',
        segment_color: undefined,
      });
      // Also trigger a local editor reset in CommentForm
      setCommentFormResetCounter((prev) => prev + 1);
      setTextFilter(undefined);
      setShowSearchField(false);
    }
  }, [assetId, currentVersionId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset intentionally runs on assetId change or unmount
  useEffect(() => {
    return () => {
      reset();
    };
  }, [assetId, reset]);

  const handleShowReplies = useCallback((segmentId: string) => {
    setShowReplies((prev) => ({
      ...prev,
      [segmentId]: !prev[segmentId],
    }));
  }, []);

  const revealReplies = useCallback((segmentId: string) => {
    setShowReplies((prev) => ({
      ...prev,
      [segmentId]: true,
    }));
  }, []);

  // Reset expansion when filters change so a new search re-expands matching
  // reply threads; a collapse from an earlier view shouldn't suppress a fresh match.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the filters are intentional triggers, not read in the body
  useEffect(() => {
    setShowReplies({});
  }, [
    annotationFilter,
    completionFilter,
    replyFilter,
    mentionFilter,
    commenterFilter,
    colorFilter,
    textFilter,
    visibilityFilter,
    versionFilter,
  ]);

  // Auto-expand reply-matched threads, but respect explicit user toggles: an
  // absent entry means untouched, while `false` means deliberately collapsed.
  const resolvedShowReplies = useMemo(() => {
    if (rootIdsWithMatchingReply.length === 0) {
      return showReplies;
    }
    const result = { ...showReplies };
    for (const rootId of rootIdsWithMatchingReply) {
      if (showReplies[rootId] === undefined) {
        result[rootId] = true;
      }
    }
    return result;
  }, [showReplies, rootIdsWithMatchingReply]);

  const commentsIds = getListIds(comments, repliesMap, resolvedShowReplies);
  const vListRef = useRef<VListHandle>(null);
  const { setScrollToIds } = useVirtualizedScrollToIds({
    virtualizer: vListRef.current,
    ids: commentsIds,
  });

  let allowComments = true;
  let allowAllUserSearch = true;
  let allowApproveComments = true;

  if (share) {
    allowComments = share.allow_comments ?? false;
    allowAllUserSearch = share.allow_user_search_for_mentions ?? false;
    allowApproveComments = share.allow_approving_comments ?? true;
  }

  const replyIdToParentId = useMemo(() => {
    const resultMap = new Map<string, string>();

    for (const [parentId, replies] of Object.entries(repliesMap)) {
      for (const reply of replies) {
        resultMap.set(reply.id, parentId);
      }
    }

    return resultMap;
  }, [repliesMap]);

  const segmentSubmitters = useMemo((): Map<string, SuggestionsPluginItem> => {
    const result = new Map<string, SuggestionsPluginItem>();
    for (const targetSegment of baseEntities) {
      if (targetSegment.user_id && targetSegment.user_info) {
        const key = `user_id:${targetSegment.user_id}`;
        if (!result.has(key)) {
          result.set(key, {
            id: key,
            label: getUserLabel(targetSegment.user_info),
          });
        }
      } else if (targetSegment.share_user_email) {
        const key = `email:${targetSegment.share_user_email}`;
        if (!result.has(key)) {
          result.set(key, { id: key, label: targetSegment.share_user_email });
        }
      }
    }
    return result;
  }, [baseEntities]);

  const segmentSubmittersRef = useRef(segmentSubmitters);
  useEffect(() => {
    segmentSubmittersRef.current = segmentSubmitters;
  }, [segmentSubmitters]);

  const [shareOwnerSuggestion, setShareOwnerSuggestion] =
    useState<SuggestionsPluginItem | null>(null);

  const shareOwnerSuggestionRef = useRef(shareOwnerSuggestion);
  useEffect(() => {
    shareOwnerSuggestionRef.current = shareOwnerSuggestion;
  }, [shareOwnerSuggestion]);

  useEffect(() => {
    if (allowAllUserSearch || !share?.owner_id) {
      setShareOwnerSuggestion(null);
      return;
    }

    let cancelled = false;
    dispatch(httpGetUsersByUserId(share.owner_id))
      .then((result) => {
        if (cancelled) {
          return;
        }
        const owner = result.body;
        if (owner?.id) {
          setShareOwnerSuggestion({
            id: `user_id:${owner.id}`,
            label: getUserLabel(owner),
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setShareOwnerSuggestion(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [share?.owner_id, allowAllUserSearch]);

  const getMentionSuggestions = useCallback(
    async (query: string | null): Promise<SuggestionsPluginItem[]> => {
      if (allowAllUserSearch) {
        const queryObject = { query: `${query ?? ''}*`, per_page: 100 };
        const results = await Promise.allSettled([
          dispatch(
            httpGetUsersBasic({
              query: `${query ?? ''}*`,
              sort: 'first_name,asc',
              per_page: 100,
            }),
          ),
          growthbook?.isOn(FLAG_SIMPLIFIED_ACLS)
            ? dispatch(httpGetTeamsBasic(queryObject))
            : dispatch(httpGetGroupsBasic(queryObject)),
        ]);
        const [usersResult, groupsResult] = results;
        const users =
          usersResult.status === 'fulfilled'
            ? usersResult.value.body.objects
            : [];
        const groups =
          groupsResult.status === 'fulfilled'
            ? groupsResult.value.body.objects
            : [];
        return [
          ...users.map((u) => ({
            id: `user_id:${u.id}`,
            label: getUserLabel(u),
          })),
          ...groups.map((g) => ({ id: `group_id:${g.id}`, label: g.name })),
        ];
      }
      const lowerQuery = (query ?? '').toLowerCase();
      const submitters = segmentSubmittersRef.current;
      const ownerSuggestion = shareOwnerSuggestionRef.current;
      const candidates = [
        ...submitters.values(),
        ...(ownerSuggestion && !submitters.has(ownerSuggestion.id)
          ? [ownerSuggestion]
          : []),
      ];
      return candidates.filter((item) =>
        item.label.toLowerCase().includes(lowerQuery),
      );
    },
    [allowAllUserSearch],
  );
  const { options: segmentFilterCommenterOptions, commenterCount } =
    useMemo(() => {
      const effectiveVersionFilter =
        asset?.type === 'SUBCLIP' ? 'all' : versionFilter;
      const versionFilteredEntities =
        effectiveVersionFilter && effectiveVersionFilter !== 'all'
          ? baseEntities.filter(
              (targetSegment) =>
                targetSegment.version_id === effectiveVersionFilter,
            )
          : baseEntities;

      const seen = new Map<
        string,
        { name: string; photo: string | undefined; initials: string }
      >();
      for (const targetSegment of versionFilteredEntities) {
        if (targetSegment.user_id && !seen.has(targetSegment.user_id)) {
          const userEntity = usersBasicById[targetSegment.user_id];
          const name =
            [targetSegment.user_first_name, targetSegment.user_last_name]
              .filter(Boolean)
              .join(' ') ||
            getUserLabel(userEntity ?? {}) ||
            targetSegment.user_id;
          const initials = nameInitials({
            first_name: targetSegment.user_first_name ?? userEntity?.first_name,
            last_name: targetSegment.user_last_name ?? userEntity?.last_name,
            email: userEntity?.email ?? undefined,
          });
          seen.set(targetSegment.user_id, {
            name,
            photo: targetSegment.user_photo,
            initials,
          });
        } else if (
          !targetSegment.user_id &&
          targetSegment.share_user_email &&
          !seen.has(targetSegment.share_user_email)
        ) {
          const initials = nameInitials({
            email: targetSegment.share_user_email,
          });
          seen.set(targetSegment.share_user_email, {
            name: targetSegment.share_user_email,
            photo: undefined,
            initials,
          });
        }
      }

      // Add ghost entries for active filter values not present on this asset,
      // so the user can see and remove what is being filtered on.
      for (const filterValue of commenterFilter) {
        if (seen.has(filterValue)) {
          continue;
        }
        const userEntity = usersBasicById[filterValue];
        if (userEntity) {
          seen.set(filterValue, {
            name: getUserLabel(userEntity) || filterValue,
            photo: userEntity.photo,
            initials: nameInitials({
              first_name: userEntity.first_name,
              last_name: userEntity.last_name,
              email: userEntity.email ?? undefined,
            }),
          });
        } else {
          seen.set(filterValue, {
            name: filterValue,
            photo: undefined,
            initials: nameInitials({ email: filterValue }),
          });
        }
      }

      const options: MenuItemWithId[] = Array.from(seen.entries())
        .sort(([, a], [, b]) => a.name.localeCompare(b.name))
        .map(([key, { name, photo, initials }]) => ({
          id: `commenter:${key}`,
          label: (
            <span className={styles.commenter_option}>
              <Avatar size="xs" src={photo} label={initials} />
              {name}
            </span>
          ),
        }));
      options.push(
        { id: 'separator_footer', isSeparator: true },
        {
          id: 'commenter:none',
          label: 'None',
          isDisabled: !commenterFilter.length,
        },
      );
      return { options, commenterCount: seen.size };
    }, [
      baseEntities,
      commenterFilter,
      usersBasicById,
      versionFilter,
      asset?.type,
    ]);

  useEffect(() => {
    setSegment((prevSegment) => ({
      ...prevSegment,
      version_id: currentVersionId,
    }));
  }, [currentVersionId]);

  useEffect(() => {
    if (!selectedSegmentIds || isLoading) {
      return;
    }

    selectedSegmentIds.forEach((segmentId) => {
      const parentId = replyIdToParentId.get(segmentId);
      if (parentId) {
        revealReplies(parentId);
      }
    });
  }, [selectedSegmentIds, isLoading, revealReplies, replyIdToParentId]);

  useEffect(() => {
    // Scroll the comments to the highlighted segments, triggered by unified player store
    if (!unifiedSelectedSegmentIds || isLoading) {
      return;
    }
    setScrollToIds([unifiedSelectedSegmentIds[0]]);
  }, [unifiedSelectedSegmentIds, isLoading]);

  const handleSearch = useCallback((value: string) => {
    setTextFilter(value);
  }, []);

  const handleFilter = useCallback((values: string[]) => {
    if (values.includes('clear_filters')) {
      setColorFilter([]);
      setAnnotationFilter([]);
      setCompletionFilter([]);
      setReplyFilter([]);
      setMentionFilter([]);
      setCommenterFilter([]);
      setVisibilityFilter([]);
      return;
    }

    if (values.includes('color:none')) {
      setColorFilter([]);
      return;
    }

    if (values.includes('annotation:none')) {
      setAnnotationFilter([]);
      return;
    }

    if (values.includes('completion:none')) {
      setCompletionFilter([]);
      return;
    }

    if (values.includes('reply:none')) {
      setReplyFilter([]);
      return;
    }

    if (values.includes('mention:none')) {
      setMentionFilter([]);
      return;
    }

    if (values.includes('commenter:none')) {
      setCommenterFilter([]);
      return;
    }

    if (values.includes('visibility:none')) {
      setVisibilityFilter([]);
      return;
    }

    setColorFilter(
      values
        .filter((v) => v.startsWith('color:'))
        .map((v) => v.slice('color:'.length)),
    );
    setAnnotationFilter(
      values
        .filter((v) => v.startsWith('annotation:'))
        .map((v) => v.slice('annotation:'.length)),
    );
    setCompletionFilter(
      values
        .filter((v) => v.startsWith('completion:'))
        .map((v) => v.slice('completion:'.length)),
    );
    setReplyFilter(
      values
        .filter((v) => v.startsWith('reply:'))
        .map((v) => v.slice('reply:'.length)),
    );
    setMentionFilter(
      values
        .filter((v) => v.startsWith('mention:'))
        .map((v) => v.slice('mention:'.length)),
    );
    setCommenterFilter(
      values
        .filter((v) => v.startsWith('commenter:'))
        .map((v) => v.slice('commenter:'.length)),
    );
    setVisibilityFilter(
      values
        .filter((v) => v.startsWith('visibility:'))
        .map((v) => v.slice('visibility:'.length)),
    );
  }, []);

  const currentVersion = currentVersionId
    ? assetVersionsMap[currentVersionId]
    : null;
  const currentPlaylistItemContentType = useStore(
    unifiedPlayerStore.playlist.$currentPlaylistItem,
  )?.contentType;

  const noComments = fetchStatus === 'idle' && comments.length === 0;
  const showVersionsDropdown =
    roles.web_can_view_versions &&
    currentVersion &&
    filteredVersionsBySelectedVersionIds.length > 1 &&
    (share == null || share.allow_view_versions);

  const versionOptions = filteredVersionsBySelectedVersionIds
    .filter((version) => version.status === 'ACTIVE' && !version.promotedTo)
    .map((version) => ({
      label: version.name,
      value: version.id,
      id: version.id,
    }))
    .toReversed()
    .concat({ label: 'All', value: 'all', id: 'all' });

  const handleChangeOrder = useCallback(
    (value: CommentSortExpression) => setSort(value),
    [],
  );

  const handleSetCommentsVersionFilter = useCallback(
    (value: string | undefined) => {
      setVersionFilter(value === 'all' ? 'all' : value);
    },
    [],
  );

  useEffect(() => {
    handleSetCommentsVersionFilter(currentVersionId);
  }, [handleSetCommentsVersionFilter, currentVersionId]);

  const handleAutoAnnotationCreated = useCallback(
    (createdSegment: AssetsSegmentSchemaWithId) => {
      appendId(createdSegment.id);
      setSegment((prevData) => ({ ...prevData, segment_text: '' }));
      // Ensure editor content resets when auto-annotation creates a segment
      setCommentFormResetCounter((prev) => prev + 1);
      onAutoAnnotationCreated?.(createdSegment);
      if (returnPlayerToPlayback) {
        unifiedPlayerStore.setActiveTool('PLAY');
        setReturnPlayerToPlayback(false);
      }
    },
    [appendId, onAutoAnnotationCreated, returnPlayerToPlayback],
  );

  useAutoAnnotation({
    assetId,
    versionId: currentVersionId,
    onCreated: handleAutoAnnotationCreated,
    shareLinkUserEmail,
    segment,
    canCreateComment,
    originalPlaylistItemVersionId,
    attachments,
  });

  // Declared after the auto-annotation hook so its save on an asset or version
  // change claims the picked files before they are cleared.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the asset and version are triggers, not read in the body
  useEffect(() => {
    resetAttachments();
    setPreviewAttachment(undefined);
  }, [assetId, currentVersionId, resetAttachments]);

  const handleSubmit = useCallback(
    async (data: AssetsSegmentSchemaPatched) => {
      const headers: AssetsHttpPostAssetsByAssetIdSegmentsHeader = {};
      if (shareLinkUserEmail) {
        headers['Share-User-Email'] = shareLinkUserEmail;
      }
      const isFormCurrent = () =>
        isMountedRef.current &&
        latestFormRef.current.assetId === assetId &&
        latestFormRef.current.currentVersionId === currentVersionId;
      const claim = claimAttachments();
      let body: AssetsSegmentSchemaWithId | undefined;
      try {
        body = await dispatch(
          sendNewComment({
            assetId,
            segment: data,
            files: claim.files.map(({ file }) => file),
            headers,
            onFailure: () => (isFormCurrent() ? 'abort' : 'publish'),
          }),
        );
      } catch (error) {
        releaseAttachments(claim);
        throw error;
      }
      completeAttachments(claim);
      if (!body) {
        return;
      }

      // A send with images can finish after the panel moved to another asset
      // or version; the list and form on screen are no longer this comment's.
      if (isFormCurrent()) {
        appendId(body.id);
        setScrollToIds([body.id]);

        onSubmitComment?.(body);

        if (returnPlayerToPlayback) {
          unifiedPlayerStore.setActiveTool('PLAY');
          setReturnPlayerToPlayback(false);
        }

        // Clear state and draft annotation after successful submission synchronously
        flushSync(() => {
          setSegment((prevData) => ({ ...prevData, segment_text: '' }));
          unifiedPlayerStore.segment.annotation.clearPersonalDraftAnnotation();
        });
      }
      dispatch(
        segmentTrackEvent({
          eventName: 'Segment Created',
          properties: {
            'Media Type': currentPlaylistItemContentType,
            'Segment Type': 'Comment',
            'Segment Color': body.segment_color,
            'Annotation Tool Used': Boolean(body.drawing),
            'Has Timecodes': Boolean(seekable),
            'Is Reply': false,
          },
        }),
      );
    },
    [
      assetId,
      currentVersionId,
      appendId,
      claimAttachments,
      completeAttachments,
      releaseAttachments,
      shareLinkUserEmail,
      onSubmitComment,
      returnPlayerToPlayback,
      currentPlaylistItemContentType,
      seekable,
    ],
  );

  const handleReplied = useCallback(
    (reply: AssetsSegmentSchemaWithId) => {
      appendId(reply.id);
      setScrollToIds([reply.id]);
      onReplyCreated?.(reply);
    },
    [appendId, onReplyCreated],
  );

  const handleFocus = useCallback(() => {
    if (activeTool !== 'PLAY') {
      return;
    }

    setReturnPlayerToPlayback(true);
    unifiedPlayerStore.setActiveTool(previousActiveTool);
  }, [activeTool, previousActiveTool]);

  if (isDisabled) {
    return (
      <div className={classNames(styles.comments_disabled_panel)}>
        <Text elementType="p">
          {share
            ? 'Comments are disabled for this share'
            : 'Comments are disabled'}
        </Text>
      </div>
    );
  }

  if (!asset) {
    return <LoadingBar />;
  }

  const filterActive = Boolean(
    colorFilter.length ||
      annotationFilter.length ||
      (allowApproveComments && completionFilter.length) ||
      replyFilter.length ||
      mentionFilter.length ||
      commenterFilter.length ||
      (isInternalUser && visibilityFilter.length),
  );

  const segmentFilterColorOptions = [
    ...segmentColors.map((item) => ({
      id: `color:${item.value}`,
      element: <ColorMenuItem {...item} />,
    })),
    {
      id: 'separator_footer',
      isSeparator: true,
    },
    {
      id: 'color:none',
      element: <ColorMenuItem value="none" label="None" />,
      isDisabled: !colorFilter.length,
    },
  ];

  const segmentFilterAnnotationOptions = [
    {
      id: 'annotation:has_annotation',
      label: 'Has Annotation',
    },
    {
      id: 'annotation:has_no_annotation',
      label: 'Has No Annotation',
    },
    {
      id: 'separator_footer',
      isSeparator: true,
    },
    {
      id: 'annotation:none',
      label: 'None',
      isDisabled: !annotationFilter.length,
    },
  ];

  const segmentFilterCompletionOptions = [
    {
      id: 'completion:completed',
      label: 'Completed',
    },
    {
      id: 'completion:not_completed',
      label: 'Not Completed',
    },
    {
      id: 'separator_footer',
      isSeparator: true,
    },
    {
      id: 'completion:none',
      label: 'None',
      isDisabled: !completionFilter.length,
    },
  ];

  const segmentFilterReplyOptions = [
    {
      id: 'reply:has_replies',
      label: 'Has Replies',
    },
    {
      id: 'reply:no_replies',
      label: 'No Replies',
    },
    {
      id: 'separator_footer',
      isSeparator: true,
    },
    {
      id: 'reply:none',
      label: 'None',
      isDisabled: !replyFilter.length,
    },
  ];

  const segmentFilterMentionOptions = [
    {
      id: 'mention:has_mention',
      label: 'Has Mention',
    },
    {
      id: 'mention:no_mention',
      label: 'No Mention',
    },
    {
      id: 'separator_footer',
      isSeparator: true,
    },
    {
      id: 'mention:none',
      label: 'None',
      isDisabled: !mentionFilter.length,
    },
  ];

  const segmentFilterVisibilityOptions: MenuItemWithId[] = [
    { id: 'visibility:public', label: 'Public' },
    { id: 'visibility:internal', label: 'Internal' },
    { id: 'separator_footer', isSeparator: true },
    {
      id: 'visibility:none',
      label: 'None',
      isDisabled: !visibilityFilter.length,
    },
  ];

  const filterLabel = (text: string, active: boolean) => {
    if (!active) {
      return text;
    }
    return (
      <span className={styles.filter_label_active}>
        {text}
        <span className={styles.filter_label_active_dot} />
      </span>
    );
  };

  const filterMenuItems: MenuItemWithId[] = [];
  filterMenuItems.push({
    label: filterLabel('Annotation', annotationFilter.length > 0),
    id: 'annotation',
    icon: 'annotation_annotation',
    submenuChildren: segmentFilterAnnotationOptions,
  });
  filterMenuItems.push({
    label: filterLabel('Color', colorFilter.length > 0),
    id: 'color',
    icon: 'annotation_color_palette',
    submenuChildren: segmentFilterColorOptions,
  });
  filterMenuItems.push({
    label: filterLabel('Commenter', commenterFilter.length > 0),
    id: 'commenter',
    icon: 'user_user',
    isDisabled: commenterCount === 0,
    submenuChildren: segmentFilterCommenterOptions,
  });
  if (allowApproveComments) {
    filterMenuItems.push({
      label: filterLabel('Completion', completionFilter.length > 0),
      id: 'completion',
      icon: 'checkmark',
      submenuChildren: segmentFilterCompletionOptions,
    });
  }
  filterMenuItems.push({
    label: filterLabel('Mention', mentionFilter.length > 0),
    id: 'mention',
    icon: 'mention',
    submenuChildren: segmentFilterMentionOptions,
  });
  filterMenuItems.push({
    label: filterLabel('Reply', replyFilter.length > 0),
    id: 'reply',
    icon: 'reply',
    submenuChildren: segmentFilterReplyOptions,
  });
  if (isInternalUser) {
    filterMenuItems.push({
      label: filterLabel('Visibility', visibilityFilter.length > 0),
      id: 'visibility',
      icon: 'user_type_browse',
      submenuChildren: segmentFilterVisibilityOptions,
    });
  }
  filterMenuItems.push(
    { id: 'separator_filters', isSeparator: true },
    {
      id: 'clear_filters',
      label: 'Clear Filters',
      icon: 'close',
      selectionMode: 'none',
    },
  );

  const buttonLabel =
    versionOptions.find((item) => item.id === versionFilter)?.label ?? 'All';

  const isCommentVersionMatchingPlayer =
    versionFilter === undefined ||
    versionFilter === currentVersionId ||
    versionFilter === 'all';

  const commentElements: ReactElement[] = [];
  for (const comment of comments) {
    const commentId = comment.id;
    const commentReplies = repliesMap[commentId];

    const versionName =
      versionFilter === 'all' && comment.version_id
        ? assetVersionsMap[comment.version_id]?.name
        : undefined;

    const isCommentInScope =
      share === undefined || share.id === comment?.share_id;

    commentElements.push(
      <Segment
        key={commentId}
        assetId={assetId}
        segment={comment}
        version={
          comment.version_id && filteredVersionsBySelectedVersionIds.length > 0
            ? assetVersionsMap[comment.version_id]
            : undefined
        }
        versionName={versionName}
        isCurrentVersion={
          !currentVersionId ||
          currentVersionId === comment.version_id ||
          !comment.version_id
        }
        timecodeOptions={proxyTimecodeOptions}
        onReplied={handleReplied}
        onShowRepliesThread={() => handleShowReplies(commentId)}
        onReplyClick={() => {
          onReplyClick?.();
          revealReplies(commentId);
        }}
        showReplies={resolvedShowReplies[commentId]}
        numberOfReplies={totalRepliesCountMap[commentId] ?? 0}
        hasReplies={(totalRepliesCountMap[commentId] ?? 0) > 0}
        shareLinkUserEmail={shareLinkUserEmail}
        canReply={allowComments}
        canEdit={getIsAllowedEditingComments({
          allowComments,
          canEditSegments: Boolean(roles.can_write_segments),
          canEditOtherUsersComments: Boolean(
            roles.can_edit_other_users_comments,
          ),
          comment,
          shareUserEmail,
          shareSessionId,
          userId,
          isInternalUser,
          isCommentInScope,
        })}
        canDelete={getIsAllowedDeletingComments({
          shareUserEmail,
          shareSessionId,
          userId,
          comment,
          canDeleteComments: Boolean(roles.can_delete_segments),
          canEditOtherUsersComments: Boolean(
            roles.can_edit_other_users_comments,
          ),
          isInternalUser,
          isCommentInScope,
        })}
        canApproveComments={allowApproveComments}
        canMarkCommentComplete={canMarkCommentComplete}
        onMarkCompleteBlocked={onMarkCompleteBlocked}
        canMention
        filterMentionResults={getMentionSuggestions}
        shouldOpenReplyForm={shouldOpenReplyForm}
        shouldShowCommentTypeBadge={isInternalUser}
        isInternalUser={isInternalUser}
        onOpenAttachment={setPreviewAttachment}
      />,
    );
    if (resolvedShowReplies[commentId] && commentReplies) {
      for (const reply of commentReplies) {
        const isReplyInScope =
          share === undefined || share.id === reply?.share_id;
        commentElements.push(
          <Segment
            key={reply.id}
            assetId={assetId}
            segment={reply}
            version={
              reply.version_id &&
              filteredVersionsBySelectedVersionIds.length > 0
                ? assetVersionsMap[reply.version_id]
                : undefined
            }
            isCurrentVersion={
              !currentVersionId ||
              currentVersionId === reply.version_id ||
              !reply.version_id
            }
            timecodeOptions={proxyTimecodeOptions}
            onReplied={handleReplied}
            shareLinkUserEmail={shareLinkUserEmail}
            canReply={allowComments}
            canDelete={getIsAllowedDeletingComments({
              shareUserEmail,
              shareSessionId,
              userId,
              comment: reply,
              canDeleteComments: Boolean(roles.can_delete_segments),
              canEditOtherUsersComments: Boolean(
                roles.can_edit_other_users_comments,
              ),
              isInternalUser,
              isCommentInScope: isReplyInScope,
            })}
            canEdit={getIsAllowedEditingComments({
              allowComments,
              canEditSegments: Boolean(roles.can_write_segments),
              canEditOtherUsersComments: Boolean(
                roles.can_edit_other_users_comments,
              ),
              comment: reply,
              userId,
              shareUserEmail,
              shareSessionId,
              isInternalUser,
              isCommentInScope: isReplyInScope,
            })}
            canMarkCommentComplete={canMarkCommentComplete}
            onMarkCompleteBlocked={onMarkCompleteBlocked}
            canMention
            filterMentionResults={getMentionSuggestions}
            shouldOpenReplyForm={shouldOpenReplyForm}
            shouldShowCommentTypeBadge={isInternalUser}
            onOpenAttachment={setPreviewAttachment}
          />,
        );
      }
    }
  }

  return (
    <div
      className={classNames(styles.comments_panel, {
        [styles.comments_panel_empty_state]:
          fetchStatus === 'idle' && comments.length === 0,
      })}
    >
      <div className={styles.header}>
        <div className={styles.filters_container}>
          {showVersionsDropdown ? (
            <MenuTrigger>
              <Button
                size="s"
                variant="tertiary"
                endIcon={<Icon name="chevron_small_isolated_s" />}
                aria-label="Filter comments by version"
              >
                {buttonLabel}
              </Button>
              <Menu
                headerTitle="Versions"
                selectionMode="single"
                disallowEmptySelection
                selection={versionFilter ?? null}
                onSelectionChange={handleSetCommentsVersionFilter}
                items={versionOptions.map((item) => {
                  return {
                    element: (
                      <div className={styles.version_item}>
                        {item.label}{' '}
                        {item.id === currentVersionId ? (
                          <TooltipTrigger>
                            <Focusable>
                              <div>
                                <Icon
                                  name="display_show"
                                  className={styles.version_item_icon}
                                />
                              </div>
                            </Focusable>
                            <Tooltip>
                              You are currently viewing this version
                            </Tooltip>
                          </TooltipTrigger>
                        ) : null}
                      </div>
                    ),
                    id: item.id,
                  };
                })}
              />
            </MenuTrigger>
          ) : null}

          <div>
            <MenuTrigger>
              <Button
                size="s"
                variant={filterActive ? 'secondary' : 'tertiary'}
                endIcon={<Icon name="chevron_small_isolated_s" />}
                aria-label="Filter comments"
              >
                Filters
              </Button>
              <Menu
                headerTitle="Filters"
                selectionMode="multiple"
                selection={[
                  ...colorFilter.map((v) => `color:${v}`),
                  ...annotationFilter.map((v) => `annotation:${v}`),
                  ...(allowApproveComments
                    ? completionFilter.map((v) => `completion:${v}`)
                    : []),
                  ...mentionFilter.map((v) => `mention:${v}`),
                  ...replyFilter.map((v) => `reply:${v}`),
                  ...commenterFilter.map((v) => `commenter:${v}`),
                  ...visibilityFilter.map((v) => `visibility:${v}`),
                ]}
                onSelectionChange={(value) => {
                  handleFilter(
                    Array.from(value).map((item) => item.toString()),
                  );
                }}
                items={filterMenuItems}
              />
            </MenuTrigger>
          </div>
          <CommentsSortMenu
            noComments={noComments}
            seekable={seekable}
            sort={sort}
            onSelectionChange={handleChangeOrder}
          />
        </div>
        <div>
          <Button
            className={classNames({
              [styles.search_button_background]: showSearchField,
            })}
            aria-label="Search comments"
            icon={<Icon name="search" />}
            variant="tertiary"
            isDisabled={noComments}
            size="m"
            onPress={() => {
              setShowSearchField((prev) => !prev);
            }}
          />
          {assetId && currentVersionId ? (
            <CommentsMenuButton
              assetId={assetId}
              currentVersionId={currentVersionId}
              isDisabled={noComments}
            />
          ) : null}
        </div>
      </div>
      {showSearchField && (
        <div className={styles.search_field_wrapper}>
          <SearchField
            onChange={handleSearch}
            className={styles.search_field}
            size="m"
            value={textFilter}
            placeholder="Search comments..."
            aria-label="Search comments"
          />
          <Button
            variant="tertiary"
            onPress={() => {
              setShowSearchField(false);
              handleSearch('');
            }}
          >
            Cancel
          </Button>
        </div>
      )}
      <div
        className={classNames(styles.list_wrapper, {
          [styles.list_wrapper_empty_state]:
            fetchStatus === 'idle' && comments.length === 0,
        })}
      >
        {fetchStatus === 'fetching' ? (
          <ProgressCircle
            aria-label="Loading transcriptions"
            size="m"
            className={styles.loading_circle}
          />
        ) : null}
        {fetchStatus === 'idle' && comments.length === 0 && !filterActive ? (
          <EmptyState
            type={isLatestVersion ? 'comments' : 'comments_older_version'}
          />
        ) : null}
        {fetchStatus === 'idle' && comments.length === 0 && filterActive ? (
          <EmptyState type="comments_filtered" />
        ) : null}
        <VList
          count={comments.length}
          itemSize={130}
          ref={vListRef}
          role="listbox"
          aria-multiselectable="true"
        >
          {commentElements}
        </VList>
      </div>

      {roles.can_create_segments &&
      isCommentVersionMatchingPlayer &&
      allowComments ? (
        <CommentForm
          onChange={handleChange}
          onSubmit={handleSubmit}
          segment={segment}
          withColorsPicker
          canMention
          filterMentionResults={getMentionSuggestions}
          onFocus={handleFocus}
          canComment={canCreateComment}
          onIdentificationRequired={onIdentificationRequired}
          resetCounter={commentFormResetCounter}
          isInternalUser={isInternalUser}
          commentVersion={currentVersionId}
          attachments={getPendingFormAttachments(attachments)}
        />
      ) : isCommentVersionMatchingPlayer ? (
        <Sheet variant="fill" className={styles.disabled_comment_form}>
          <Text elementType="p">
            {share && !allowComments
              ? 'New comments are disabled for this share'
              : 'New comments are disabled'}
          </Text>
        </Sheet>
      ) : null}

      {/* Laid over the list instead of replacing it, so open reply and edit
      forms stay mounted and don't auto-save. */}
      {previewAttachment ? (
        <CommentAttachmentPreview
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(undefined)}
        />
      ) : null}
    </div>
  );
}
